
import { useEffect, useMemo } from 'react';
import AppCard from '../components/AppCard';
import AppListItem from '../components/AppListItem';
import RequestAppModal from '../components/RequestAppModal';
import BuyModal from '../components/BuyModal';
import TrialModal from '../components/TrialModal';
import { useAppStore } from '../services/store';

export default function StorePage() {
  const {
    searchTerm,
    selectedCategory,
    sortBy,
    viewMode,
    currentPage,
    itemsPerPage,
    isLoading,
    error,
    categories: apiCategories,
    setSearchTerm,
    setSelectedCategory,
    setSortBy,
    setViewMode,
    setCurrentPage,
    setItemsPerPage,
    setShowRequestModal,
    getPaginatedApps,
    loadInitialData
  } = useAppStore();

  const { apps: paginatedApps, totalPages, total } = getPaginatedApps();

  // Create categories array from API data
  const categories = useMemo(() => {
    const categoryNames = apiCategories.map(cat => cat.app_ctgry_nm);
    return ['All', ...categoryNames];
  }, [apiCategories]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Skeleton loader component for cards
  const AppCardSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden animate-pulse">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-5">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6 mb-4"></div>
        <div className="flex items-center gap-4 mb-4">
          <div className="h-4 bg-gray-200 rounded w-20"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="flex gap-2">
          <div className="h-9 bg-gray-200 rounded flex-1"></div>
          <div className="h-9 bg-gray-200 rounded w-9"></div>
        </div>
      </div>
    </div>
  );

  // Skeleton loader component for list items
  const AppListSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
      <div className="flex gap-6">
        <div className="w-32 h-32 bg-gray-200 rounded-lg flex-shrink-0"></div>
        <div className="flex-1">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-4/5 mb-4"></div>
          <div className="flex items-center gap-6 mb-4">
            <div className="h-4 bg-gray-200 rounded w-20"></div>
            <div className="h-4 bg-gray-200 rounded w-24"></div>
            <div className="h-4 bg-gray-200 rounded w-28"></div>
          </div>
          <div className="flex gap-3">
            <div className="h-10 bg-gray-200 rounded w-32"></div>
            <div className="h-10 bg-gray-200 rounded w-28"></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header Skeleton - Full width */}
        <div className="bg-gradient-to-br from-blue-400 to-blue-500 shadow-xl animate-pulse">
          <div className="px-8 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl"></div>
                <div>
                  <div className="h-7 bg-white/20 rounded w-32 mb-2"></div>
                  <div className="h-4 bg-white/20 rounded w-72"></div>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-6 bg-white/10 rounded-lg px-5 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-white/20 rounded"></div>
                  <div>
                    <div className="h-6 bg-white/20 rounded w-8 mb-1"></div>
                    <div className="h-3 bg-white/20 rounded w-10"></div>
                  </div>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-white/20 rounded"></div>
                  <div>
                    <div className="h-6 bg-white/20 rounded w-6 mb-1"></div>
                    <div className="h-3 bg-white/20 rounded w-16"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 py-6">
          {/* Filters Skeleton */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-300 p-6 mb-6 animate-pulse">
            <div className="flex flex-col lg:flex-row gap-4 mb-6">
              <div className="flex-1 h-12 bg-gray-200 rounded-lg"></div>
              <div className="flex gap-3">
                <div className="w-40 h-12 bg-gray-200 rounded-lg"></div>
                <div className="w-32 h-12 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="h-10 w-28 bg-gray-200 rounded-full"></div>
              ))}
            </div>
          </div>

          {/* Controls Skeleton */}
          <div className="mb-6 flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-4">
              <div className="h-5 bg-gray-200 rounded w-48"></div>
              <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                <div className="w-10 h-10 bg-gray-200"></div>
                <div className="w-10 h-10 bg-gray-100"></div>
              </div>
            </div>
            <div className="h-10 bg-gray-200 rounded-lg w-36"></div>
          </div>

          {/* Apps Grid Skeleton */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, i) => (
              <AppCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-600"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Apps</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => loadInitialData()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-8">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
        </button>
        
        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2 text-gray-500">...</span>}
          </>
        )}

        {pages.map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`px-4 py-2 border rounded-lg transition-colors whitespace-nowrap ${
              currentPage === page
                ? 'bg-blue-600 text-white border-blue-600'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2 text-gray-500">...</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section - Full width, edge to edge */}
      <div className="relative overflow-hidden module-header shadow-xl">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.1))]"></div>
        <div className="relative px-8 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <i className="ri-store-2-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">App Store</h1>
                <p className="text-sm text-blue-100">Discover and install powerful applications for your organization</p>
              </div>
            </div>

            <div className="hidden sm:flex items-center gap-6 bg-white/10 backdrop-blur-sm rounded-lg px-5 py-3 border border-white/20">
              <div className="flex items-center gap-2">
                <i className="ri-apps-2-line text-lg text-white/80"></i>
                <div>
                  <div className="text-xl font-bold text-white">{total}</div>
                  <div className="text-xs text-blue-100">Apps</div>
                </div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="flex items-center gap-2">
                <i className="ri-folder-line text-lg text-white/80"></i>
                <div>
                  <div className="text-xl font-bold text-white">{categories.length - 1}</div>
                  <div className="text-xs text-blue-100">Categories</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-300 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="Search apps by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 pr-8"
              >
                <option value="popular">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="name">Name A-Z</option>
                <option value="newest">Newest</option>
              </select>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 pr-8"
              >
                <option value={12}>12 per page</option>
                <option value={24}>24 per page</option>
                <option value={48}>48 per page</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <p className="text-gray-600">
              Showing <span className="font-semibold">{paginatedApps.length}</span> of <span className="font-semibold">{total}</span> app{total !== 1 ? 's' : ''}
              {selectedCategory !== 'All' && ` in ${selectedCategory}`}
              {searchTerm && ` matching "${searchTerm}"`}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">View:</span>
              <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('card')}
                  className={`px-3 py-2 text-sm transition-colors ${
                    viewMode === 'card'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-grid-line"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 text-sm transition-colors ${
                    viewMode === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-list-unordered"></i>
                </button>
              </div>
            </div>
          </div>
          <button 
            onClick={() => setShowRequestModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm whitespace-nowrap flex items-center"
          >
            <i className="ri-add-line mr-2"></i>
            Request New App
          </button>
        </div>

        {viewMode === 'card' ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {paginatedApps.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedApps.map((app) => (
              <AppListItem key={app.id} app={app} />
            ))}
          </div>
        )}

        {paginatedApps.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-search-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">No apps found</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Try adjusting your search terms or browse different categories to find what you're looking for
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                }}
                className="text-blue-600 font-medium hover:text-blue-700 whitespace-nowrap"
              >
                Clear all filters
              </button>
              <button
                onClick={() => setShowRequestModal(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Request this app
              </button>
            </div>
          </div>
        )}

        {renderPagination()}
      </div>

      <RequestAppModal />
      <BuyModal />
      <TrialModal />
    </div>
  );
}
